/**
 * The Strip Card setup step's draft, its "what is still missing" list, and
 * the `stripCardSetup` payload `/pool/create` carries (ISSUE-1258,
 * ISSUE-1287).
 *
 * Stage A contract: the shape and the three functions are what
 * `features/createPool` calls through `poolTypeSetups.ts`. The web builder
 * owns this file and the step component; the create flow never changes again
 * when they do. The game picker reads `useEventDaysWithGamesList` and
 * `useEventList` from `@sportsmet/events-sdk/private` for the pool's league
 * (days from today on, in the fan's own time zone). contest-api checks the
 * game again at create: it belongs to the league and has not started.
 */
import { STRIP_DEFAULT_MAX_SPOTS_PER_MEMBER } from "@sportsmet/contest-engine";
import type { usePoolCreate } from "@sportsmet/pool-sdk/private";

import {
  STRIP_MAX_SPOTS_LABEL,
  STRIP_SETUP_CUSTOM_FIRST_GAME_DEFAULT,
  STRIP_SETUP_FIRST_GAME_HEADING,
  stripMaxSpotsChoiceLabel,
} from "./stripCardCopy";

type typePoolCreateRequest = Parameters<
  ReturnType<typeof usePoolCreate>["mutateAsync"]
>[0];

/** Exactly the `stripCardSetup` object `/pool/create` accepts. */
export type typeStripCardSetupPayload = NonNullable<
  typePoolCreateRequest["stripCardSetup"]
>;

export type typeStripCardSetupDraft = {
  /** The day picked in the game picker, YYYY-MM-DD in the fan's time zone. */
  day: string | null;
  /** The first game. Its card opens with strip 1 when the pool is created. */
  eventId: string | null;
  /** The first game as the picker showed it, for the Review step only. */
  gameLabel: string | null;
  /** Spots one member may hold on one strip. Null is Unlimited. */
  maxSpotsPerMember: number | null;
  /**
   * ISSUE-1299. True when the pool has no league (the Custom tile). Set by
   * the create flow's chooseLeague. The first game is then a custom game.
   */
  isCustomLeague: boolean;
  /** ISSUE-1299. The first custom game's name; blank lets the server name it. */
  customGameTitle: string;
};

export const EMPTY_STRIP_CARD_SETUP_DRAFT: typeStripCardSetupDraft = {
  day: null,
  eventId: null,
  gameLabel: null,
  // v1's long-standing cap of 4, kept as the starting choice.
  maxSpotsPerMember: STRIP_DEFAULT_MAX_SPOTS_PER_MEMBER,
  isCustomLeague: false,
  customGameTitle: "",
};

/** Plain-English items the create footer lists while Create is disabled. */
export function stripCardSetupMissingItems(
  draft: typeStripCardSetupDraft,
): readonly string[] {
  if (draft.isCustomLeague) return [];
  return draft.eventId === null ? ["the first game"] : [];
}

/** The `stripCardSetup` payload, or null while anything is missing. */
export function buildStripCardSetupPayload(
  draft: typeStripCardSetupDraft,
): typeStripCardSetupPayload | null {
  if (draft.isCustomLeague) {
    // ISSUE-1299. No schedule game: the server makes the first custom game.
    const title = draft.customGameTitle.replace(/\s+/g, " ").trim();
    return {
      customGameTitle: title === "" ? null : title,
      maxSpotsPerMember: draft.maxSpotsPerMember,
    };
  }
  if (draft.eventId === null) {
    return null;
  }
  return {
    eventId: draft.eventId,
    maxSpotsPerMember: draft.maxSpotsPerMember,
  };
}

/**
 * The Review step's Strip Card rows (QA 2026-11-02): the first game and the
 * spots cap, so the Commissioner sees both before Create.
 */
export function stripCardSetupReviewRows(
  draft: typeStripCardSetupDraft,
): { dt: string; dd: string }[] {
  return [
    {
      dt: STRIP_SETUP_FIRST_GAME_HEADING,
      dd: draft.isCustomLeague
        ? draft.customGameTitle.replace(/\s+/g, " ").trim() ||
          STRIP_SETUP_CUSTOM_FIRST_GAME_DEFAULT
        : (draft.gameLabel ?? ""),
    },
    {
      dt: STRIP_MAX_SPOTS_LABEL,
      dd: stripMaxSpotsChoiceLabel(draft.maxSpotsPerMember),
    },
  ];
}
